/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import axios from "axios";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { ErrorMessage } from "@hookform/error-message";
import styled, { css } from "styled-components";
import { useScreenClass } from "react-grid-system";
import MinimalSelect from "./MinimalSelect";
import FlexBox, { FlexGrow } from "./FlexBox";
import theme from "../styles/theme";
import Card from "./Card";
import { StyledText } from "./Text";
import Input from "./Input";
import TextArea from "./TextArea";
import Button from "./Button";
import { RightAlign } from "./UtilsComponents";
import Icon, { ClickableIcon } from "./Icon";
import Status from "../types/enum/Status";
import Modal from "./Modal";
import convertToSelect from "../hooks/convertToSelect";
import { Switch } from "@mui/material";
import moment from "moment";

const sharedMinimalStyle = css`å
  width: 100%;
  border-color: hsl(0, 0%, 80%);
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  padding: 0px 12px;
  .react-select__menu {
    width: 100%;
    min-width: unset;
    margin-left: -12px;
  }
`;
const StyledMinimalSelect = styled(MinimalSelect)`
  ${sharedMinimalStyle}
`;
type TermForm = {
  name: string | undefined;
  term_start: string | undefined;
  term_end: string | undefined;
  course_add_drop_start: string | undefined;
  course_add_deadline: string | undefined;
  course_drop_deadline: string | undefined;
  course_withdraw_deadline: string | undefined;
};
type TermEditModalProps = {
  isOpen?: boolean;
  setOpen?: (x: boolean) => void;
  currentTerm: {
    [key: string]: any;
  };
  onEditComplete?: () => void;
  setMessage: (message: string) => void;
};

const TermEditModal = ({
  isOpen,
  setOpen,
  currentTerm,
  onEditComplete,
  setMessage,
}: TermEditModalProps): JSX.Element => {
  const validationSchema = Yup.object({
    name: Yup.string().required("Term name is required"),
  });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<TermForm>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      name: currentTerm.name,
    },
  });
  dayjs.extend(utc);
  dayjs.extend(tz);
  const screenClass = useScreenClass();
  const [loading, setLoading] = useState<boolean>(false);
  const watchAllFields = watch();
  const history = useHistory();
  const [term_start, setTermStart] = useState<Dayjs | null>(
    dayjs(currentTerm.term_start).utc()
  );
  const [term_end, setTermEnd] = useState<Dayjs | null>(
    dayjs(currentTerm.term_end).utc()
  );
  const [course_add_drop_start, setAddCourseStart] = useState<Dayjs | null>(
    dayjs(currentTerm.course_add_drop_start).utc()
  );
  const [course_add_deadline, setCourseAddDeadline] = useState<Dayjs | null>(
    dayjs(currentTerm.course_add_deadline).utc()
  );
  const [course_drop_deadline, setCourseDropDeadline] = useState<Dayjs | null>(
    dayjs(currentTerm.course_drop_deadline).utc()
  );
  const [
    course_withdraw_deadline,
    setCourseWithdrawDeadline,
  ] = useState<Dayjs | null>(dayjs(currentTerm.course_withdraw_deadline).utc());
  const [isTermOpen, setIsTermOpen] = useState<boolean>(false);
  const [isOpenError, setOpenError] = useState<boolean>(false);
  const onSubmitClicked = (data: any) => {
    setLoading(true);
    const editData = {
      name: data.name,
      term_start,
      term_end,
      course_add_drop_start,
      course_add_deadline,
      course_drop_deadline,
      course_withdraw_deadline,
      is_open: isTermOpen,
    };
    axios
      .put(`/api/v1/courses/terms/${currentTerm.id}`, editData)
      .then((res) => {
        setLoading(false);
        onEditComplete?.();
        setMessage(res.data.data.message);
      })
      .catch((error) => {
        setOpenError(true);
        console.log(error);
        setLoading(false);
      });
  };
  const onErrorCloseModal = () => {
    setOpenError(false);
  };
  const onError = (errors: any, e: any) => console.log("error", errors, e);

  useEffect(() => {
    if (currentTerm.is_open) {
      setIsTermOpen(true);
    } else {
      setIsTermOpen(false);
    }
  }, []);
  return (
    <>
      <Modal isOpen={!!isOpen} setOpen={setOpen}>
        <FlexBox
          style={{
            marginBottom: theme.spacing.base * 3,
          }}
        >
          <FlexGrow>
            <h3 style={{ marginRight: "auto", marginBottom: 0 }}>Edit Term</h3>
          </FlexGrow>
          <RightAlign>
            <ClickableIcon name="x-s" onClick={() => setOpen?.(false)} />
          </RightAlign>
        </FlexBox>
        {!loading ? (
          <form onSubmit={handleSubmit(onSubmitClicked, onError)}>
            <FlexBox
              alignItems="flex-start"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow>
                <h3>Term Information</h3>
              </FlexGrow>
            </FlexBox>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Term Name
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <Input {...register("name")} isFullWidth />
                  <ErrorMessage
                    errors={errors}
                    name="name"
                    render={({ message }) => (
                      <p
                        style={{
                          marginTop: 0,
                          color: theme.color.danger,
                        }}
                      >
                        {message}
                      </p>
                    )}
                  />
                </>
              </FlexGrow>
            </FlexBox>
            <FlexBox>
              <FlexGrow>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Term Start Date
                </StyledText>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD-MM-YYYY"
                    openTo="year"
                    views={["year", "month", "day"]}
                    value={term_start}
                    onChange={(newValue) => {
                      setTermStart(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        helperText={null}
                        inputProps={{
                          ...params.inputProps,
                          readOnly: true,
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <ErrorMessage
                  errors={errors}
                  name="term_start"
                  render={({ message }) => (
                    <p
                      style={{
                        marginTop: 0,
                        color: theme.color.danger,
                      }}
                    >
                      {message}
                    </p>
                  )}
                />
              </FlexGrow>
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Term End Date
                </StyledText>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD-MM-YYYY"
                    openTo="year"
                    views={["year", "month", "day"]}
                    value={term_end}
                    onChange={(newValue) => {
                      setTermEnd(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        helperText={null}
                        inputProps={{
                          ...params.inputProps,
                          readOnly: true,
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <ErrorMessage
                  errors={errors}
                  name="term_end"
                  render={({ message }) => (
                    <p
                      style={{
                        marginTop: 0,
                        color: theme.color.danger,
                      }}
                    >
                      {message}
                    </p>
                  )}
                />
              </FlexGrow>
            </FlexBox>
            <FlexGrow grow={1}>
              <StyledText color={"#000000"} fontWeight={"bold"}>
                Course registration opens
              </StyledText>
            </FlexGrow>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD-MM-YYYY"
                openTo="year"
                value={course_add_drop_start}
                onChange={(newValue) => {
                  setAddCourseStart(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    {...params}
                    helperText={null}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <ErrorMessage
              errors={errors}
              name="course_add_drop_start"
              render={({ message }) => (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.danger,
                  }}
                >
                  {message}
                </p>
              )}
            />
            <FlexGrow grow={1}>
              <StyledText color={"#000000"} fontWeight={"bold"}>
                Course Add Deadline
              </StyledText>
            </FlexGrow>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD-MM-YYYY"
                openTo="year"
                value={course_add_deadline}
                onChange={(newValue) => {
                  setCourseAddDeadline(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    {...params}
                    helperText={null}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <ErrorMessage
              errors={errors}
              name="course_add_deadline"
              render={({ message }) => (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.danger,
                  }}
                >
                  {message}
                </p>
              )}
            />
            <FlexGrow grow={1}>
              <StyledText color={"#000000"} fontWeight={"bold"}>
                Course Drop Deadline
              </StyledText>
            </FlexGrow>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD-MM-YYYY"
                openTo="year"
                value={course_drop_deadline}
                onChange={(newValue) => {
                  setCourseDropDeadline(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    {...params}
                    helperText={null}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <ErrorMessage
              errors={errors}
              name="course_drop_deadline"
              render={({ message }) => (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.danger,
                  }}
                >
                  {message}
                </p>
              )}
            />

            <FlexGrow grow={1}>
              <StyledText color={"#000000"} fontWeight={"bold"}>
                Course Withdraw Deadline
              </StyledText>
            </FlexGrow>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD-MM-YYYY"
                openTo="year"
                value={course_withdraw_deadline}
                onChange={(newValue) => {
                  setCourseWithdrawDeadline(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    {...params}
                    helperText={null}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <ErrorMessage
              errors={errors}
              name="course_withdraw_deadline"
              render={({ message }) => (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.danger,
                  }}
                >
                  {message}
                </p>
              )}
            />

            <FlexGrow
              grow={1}
              style={{
                marginTop: theme.spacing.x2,
                border: "1px solid #c5c5c5",
                borderRadius: "4px",
                padding: "8px 20px",
                justifyContent: "space-between",
                display: "flex",
                alignItems: "center",
              }}
            >
              <StyledText color={"#000000"} fontWeight={"bold"}>
                Is Open?
              </StyledText>
              <div
                style={{ display: "flex", alignItems: "center", gap: "1px" }}
              >
                <Icon
                  style={{ fontSize: 16 }}
                  name="x-s"
                  color={!isTermOpen ? "red" : "gray"}
                />{" "}
                {/* X icon for "No" */}
                <Switch
                  color="error"
                  checked={isTermOpen} // Bind the switch state to isOpen
                  onChange={(e) => {
                    setIsTermOpen(e.target.checked); // Update the isOpen state
                  }}
                />
                <Icon
                  style={{ fontSize: 20 }}
                  name="check-mark"
                  color={isTermOpen ? "red" : "gray"}
                />{" "}
                {/* Check mark for "Yes" */}
              </div>
            </FlexGrow>

            <FlexBox
              alignItems="center"
              justify="flex-end"
              style={{
                marginTop: theme.spacing.x4,
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow>
                <RightAlign>
                  <Button
                    type="button" // Add this line
                    onClick={() => {
                      setOpen?.(false);
                    }}
                    isOutline
                    status={Status.primary}
                    style={{ padding: "4px 12px" }}
                  >
                    <Icon name="x-s" mr={0.5} />
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      !term_start?.isValid() ||
                      !term_end?.isValid() ||
                      !course_add_drop_start?.isValid() ||
                      !course_add_deadline?.isValid() ||
                      !course_drop_deadline?.isValid() ||
                      !course_withdraw_deadline?.isValid()
                    }
                    type="submit"
                    onClick={() => handleSubmit(onSubmitClicked)}
                    status={Status.primary}
                    backgroundColor="#CB092B"
                    style={{ padding: "4px 12px", marginLeft: "8px" }}
                  >
                    <Icon name="floppy" mr={0.5} />
                    Save
                  </Button>
                </RightAlign>
              </FlexGrow>
            </FlexBox>
          </form>
        ) : (
          <Icon mr={1} name="spinner" spin />
        )}
      </Modal>
      <Modal
        isOpen={isOpenError}
        setOpen={setOpenError}
        onClose={onError}
        customStyle={{ content: { textAlign: "center" } }}
      >
        <img
          src="/alert-warning.svg"
          alt="alert warning"
          style={{ margin: "auto" }}
        />
        <h3>Cannot Edit Term</h3>
        <p>
          An error occurred while editing the term. This error can occur if the
          same term name already exist. Please try again or contact us if the
          problem persists.
        </p>
        <Button status={Status.primary} onClick={onErrorCloseModal}>
          Done
        </Button>
      </Modal>
    </>
  );
};

export default TermEditModal;
